<template>
  <div>
    <div class="row" :class="{'disabled-form': creatingPaymentMethod}">
      <div class="col s12 m4">
        <label for="cardNumber">Card Number</label>
        <div id="cardNumber" ref="cardNumber" class="stripe-input" :class="{'disabled-input': creatingPaymentMethod}"></div>
      </div>

      <div class="col s4 m2">
        <label for="cardExpiry">Exp Date</label>
        <div id="cardExpiry" ref="cardExpiry" class="stripe-input" :class="{'disabled-input': creatingPaymentMethod}"></div>
      </div>

      <div class="col s4 m2">
        <label for="cardCvc">CVC</label>
        <div id="cardCvc" ref="cardCvc" class="stripe-input" :class="{'disabled-input': creatingPaymentMethod}"></div>
      </div>

      <div class="col s4 m2">
        <label for="addressZip">{{ zipLabel }}</label>
        <input id="addressZip" ref="addressZip" class="zip-input" :class="{'disabled-input': creatingPaymentMethod}" type="text" v-mask="zipMask" v-model="addressZip"
          :style="{ color: foregroundColor }" placeholder="90210" v-validate="'required|length:' + zipLength"
          @input="onZipChange" data-vv-name="postalCode" :disabled="creatingPaymentMethod" />
      </div>
    </div>

    <turnstile
      v-if="requireCaptcha"
      ref="turnstile"
      :site-key="turnstileSiteKey"
      :theme="turnstileTheme"
      appearance="interaction-only"
      @verified="onTurnstileVerified"
      @expired="onTurnstileExpired"
      @error="onTurnstileError">
    </turnstile>

    <div v-if="creatingPaymentMethod" class="!verification-status center">
      <i class="fas fa-spinner fa-spin grey-text"></i>
      <span class="grey-text">Setting up payment method...</span>
    </div>

    <div v-if="captchaNotice" class="grey-text center">{{ captchaNotice }}</div>
    <div v-if="captchaError" class="red-text center">{{ captchaError }}</div>
    <div id="card-errors" class="red-text text-darken-3">{{ cardError }}</div>
    <div class='red-text'>{{ errorMessage }}</div>
    <div class='red-text'>{{ processingErrorMessage }}</div>
  </div>
</template>
<script>
import Turnstile from '../../../turnstile'

// The widget renders 'interaction-only', so usually there is nothing on screen to
// complete -- but Cloudflare does escalate to a visible checkbox, and then the
// customer has to be told. One line has to serve both, so it names the checkbox
// conditionally rather than instructing an action on an invisible element.
const CAPTCHA_PENDING_MESSAGE = 'Verifying you are a real person. If a checkbox appears, please tick it to continue.'
const CAPTCHA_FAILED_MESSAGE = 'We could not confirm you are a real person. Please refresh the page and try again.'
const CAPTCHA_TIMEOUT_MESSAGE = 'Verification did not finish. Please try again.'

// Only ever armed after we ask Cloudflare to re-solve, and an escalated challenge
// is human-paced, so this has to outlast someone finding and ticking a checkbox.
// It is a backstop for a reset that silently does nothing, not a progress deadline:
// a widget that fails outright reports it through 'error' far sooner than this.
const CAPTCHA_WAIT_MS = 90000

export default {
  components: { Turnstile },
  props: {
    publishableKey: {
      type: String,
      required: true
    },
    qid: {
      type: String,
      required: true
    },
    presetAddressZip: {
      type: String,
      default: ''
    },
    foregroundColor: {
      type: String,
      default: '#32325d'
    },
    // Anti card-testing gate, driven by a per-merchant admin setting (issue #178).
    requireCaptcha: {
      type: Boolean,
      default: false
    },
    turnstileSiteKey: {
      type: String,
      default: ''
    },
  },
  mounted() {
    this.loadStripeJs()
    this.$bus.$on('regenerate-card-token', this.regenerateToken)
  },
  beforeDestroy() {
    this.$bus.$off('regenerate-card-token', this.regenerateToken)
    this.clearCaptchaTimeout()
  },
  data() {
    return {
      errorMessage: null,
      processingErrorMessage: null,
      cardError: null,
      cardNumberComplete: false,
      cardExpiryComplete: false,
      cardCvcComplete: false,
      postalCodeComplete: false,
      currentCardBrand: '',
      addressZip: this.presetAddressZip || "",
      creatingPaymentMethod: false,
      setupIntentClientSecret: null,
      turnstileToken: null,
      // "we still need a fresh token", cleared only when one actually arrives.
      turnstileNeedsRefresh: false,
      // "a re-solve has been asked for and we are waiting on its outcome", so
      // repeated field edits do not restart a challenge already in progress.
      captchaResetPending: false,
      captchaTimeoutId: null,
      awaitingCaptcha: false,
      captchaError: null,
      captchaNotice: null
    }
  },
  methods: {
    loadStripeJs() {
      this.script = document.createElement('script')
      this.script.setAttribute('src', 'https://js.stripe.com/v3/')
      document.head.appendChild(this.script)
      this.script.onload = this.setupStripe
    },

    async setupStripe() {
      this.stripe = Stripe(this.publishableKey);
      
      this.setupCardElements();
    },
    
    onTurnstileVerified(token) {
      // reset() and expiry both report a null token -- that is the widget clearing
      // itself, not a solve.
      if (!token) {
        return;
      }

      // Cleared here, on arrival, and nowhere else. Clearing it when a reset was
      // merely *requested* was the defect: a reset that silently did nothing left
      // "we need a token" false and "we have a token" null, a state from which no
      // later attempt would ever ask for another solve.
      this.clearCaptchaTimeout();
      this.turnstileToken = token;
      this.turnstileNeedsRefresh = false;
      this.captchaResetPending = false;
      this.captchaError = null;
      this.captchaNotice = null;

      // The card fields finished first and we parked the submit waiting for this.
      // Cloudflare may have needed the customer to click a checkbox, which is
      // human-paced, so the token has to be able to drive the submit rather than
      // only the other way round.
      if (this.awaitingCaptcha) {
        this.awaitingCaptcha = false;
        // Via the completeness check, not confirmSetupIntent directly: the customer
        // may have cleared a field while solving the challenge.
        this.checkAndConfirmSetupIntent();
      }
    },

    onTurnstileExpired() {
      // Tokens die after ~300s, which a customer filling the form slowly will hit.
      this.turnstileToken = null;
      this.turnstileNeedsRefresh = true;
    },

    onTurnstileError(error) {
      console.error('Turnstile error:', error);
      this.clearCaptchaTimeout();
      this.turnstileToken = null;
      // Re-arm. Leaving this false made the component structurally unable to ask
      // for another challenge, so one transient error killed card entry for good.
      this.turnstileNeedsRefresh = true;
      this.captchaResetPending = false;
      this.captchaNotice = null;
      this.captchaError = CAPTCHA_FAILED_MESSAGE;
      this.notifyError(CAPTCHA_FAILED_MESSAGE);
      // awaitingCaptcha is deliberately left alone. Cloudflare retries recoverable
      // errors on its own, so a token may still arrive; dropping the parked submit
      // here would leave a completed card form inert even after it recovered.
    },

    // Returns true when a create may proceed. When it returns false the submit is
    // parked, and onTurnstileVerified resumes it as soon as a token lands.
    //
    // A Turnstile token is single-use and this component deliberately creates a
    // fresh Setup Intent for every tokenization attempt, so each create needs its
    // own token -- otherwise the first card attempt works and every retry after it
    // is rejected as timeout-or-duplicate.
    ensureTurnstileToken() {
      if (!this.requireCaptcha) {
        return true;
      }

      if (this.turnstileToken) {
        return true;
      }

      this.awaitingCaptcha = true;

      // Park either way. If the widget has already reported an error that message
      // stays on screen, but Cloudflare may still recover, and onTurnstileVerified
      // resumes the submit if it does. Latching a permanent refusal here meant a
      // slow script load bricked card entry until a full page reload.
      if (!this.captchaError) {
        this.captchaNotice = CAPTCHA_PENDING_MESSAGE;
      }

      // Only reset once a token has actually been spent. Resetting a widget that
      // has never solved would restart a challenge the customer is mid-way through.
      // The flag is NOT consumed here -- it stays set until a token arrives, so a
      // reset that fails or quietly does nothing can still be retried.
      if (this.turnstileNeedsRefresh && !this.captchaResetPending) {
        this.requestCaptchaRefresh();
      }

      // Guarded: requestCaptchaRefresh can resolve or fail synchronously. Arming
      // regardless meant a token that arrived during reset() still left a timer
      // running, which fired long after the token had been spent and painted an
      // error over a card entry that had already succeeded.
      if (!this.turnstileToken && !this.captchaError) {
        this.armCaptchaTimeout();
      }

      return false;
    },

    requestCaptchaRefresh() {
      const widget = this.$refs.turnstile;

      if (!widget) {
        this.failCaptcha(CAPTCHA_FAILED_MESSAGE);
        return;
      }

      // A new challenge deserves a full window. Without this the deadline stayed
      // anchored to the first park, so a later re-solve could inherit seconds.
      this.clearCaptchaTimeout();
      this.captchaResetPending = true;

      // A false return is a synchronous failure; the widget also emits 'error',
      // which re-arms us. A true return only means Cloudflare accepted the request,
      // so the timeout below remains the backstop for a reset that does nothing.
      if (!widget.reset()) {
        this.captchaResetPending = false;
      }
    },

    armCaptchaTimeout() {
      if (this.captchaTimeoutId) {
        return;
      }

      this.captchaTimeoutId = setTimeout(() => {
        this.captchaTimeoutId = null;

        if (this.turnstileToken) {
          return;
        }

        this.failCaptcha(CAPTCHA_TIMEOUT_MESSAGE);
      }, CAPTCHA_WAIT_MS);
    },

    clearCaptchaTimeout() {
      if (this.captchaTimeoutId) {
        clearTimeout(this.captchaTimeoutId);
        this.captchaTimeoutId = null;
      }
    },

    // Give up on the current solve without asserting anything about the widget.
    // turnstileNeedsRefresh is deliberately NOT set here: it already carries the
    // truth (set when a token is spent, on expiry, and on error) and is never
    // consumed, so the retry path survives on its own. Forcing it true also
    // claimed a re-solve was needed when the widget had never solved at all --
    // which made the customer's next edit reset() a visible challenge they were
    // half-way through, every 90 seconds, forever.
    failCaptcha(message) {
      this.clearCaptchaTimeout();
      this.captchaResetPending = false;
      this.captchaNotice = null;
      this.captchaError = message;
      this.notifyError(message);
    },

    async createSetupIntent() {
      const payload = {
        publishable_key: this.publishableKey,
        qid: this.qid,
        usage: "off_session",
        payment_method_types: ["card"]
      };

      if (this.turnstileToken) {
        // Spend it: single-use, so the next attempt must acquire a fresh one.
        payload.turnstile_token = this.turnstileToken;
        this.turnstileToken = null;
        this.turnstileNeedsRefresh = true;
      }

      try {
        // Create Setup Intent via Public API
        const response = await this.$http.post("/api/v1/public/setup_intents", payload);

        if (response.data.errors.length > 0) {
          this.processingErrorMessage = response.data.errors[0];
          return;
        }

        this.setupIntentClientSecret = response.data.client_secret;
        
      } catch (error) {
        if (error.status === 429) {
          this.processingErrorMessage = 'Too many requests. Please wait a moment and try again.';
        } else if (error.status === 400) {
          this.processingErrorMessage = 'Invalid request. Please refresh the page and try again.';
        } else if (error.body?.errors?.[0]) {
          this.processingErrorMessage = error.body.errors[0];
        } else {
          this.processingErrorMessage = 'Unable to process card. Please try again later.';
        }
        this.notifyError(this.processingErrorMessage);
      }
    },
    
    setupCardElements() {
      this.elements = this.stripe.elements({ locale: "en" });

      // Create individual elements for card number, expiry, CVC
      this.cardNumber = this.elements.create("cardNumber", {
        disableLink: true,
        style: {
          base: {
            color: this.foregroundColor,
            fontWeight: "500",
            fontFamily: "Roboto, Open Sans, Segoe UI, sans-serif",
            fontSize: "14px",
            iconColor: "#c4f0ff",
            "::placeholder": { color: "#aab7c4" },
          },
          invalid: { color: "#fa755a", iconColor: "#fa755a" },
        },
      });

      this.cardExpiry = this.elements.create("cardExpiry", {
        style: {
          base: {
            color: this.foregroundColor,
            fontWeight: "500",
            fontFamily: "Roboto, Open Sans, Segoe UI, sans-serif",
            fontSize: "14px",
            "::placeholder": { color: "#aab7c4" },
          },
          invalid: { color: "#fa755a" },
        },
      });

      this.cardCvc = this.elements.create("cardCvc", {
        style: {
          base: {
            color: this.foregroundColor,
            fontWeight: "500",
            fontFamily: "Roboto, Open Sans, Segoe UI, sans-serif",
            fontSize: "14px",
            "::placeholder": { color: "#aab7c4" },
          },
          invalid: { color: "#fa755a" },
        },
      });
      
      // Mount each element to its respective container
      this.cardNumber.mount(this.$refs.cardNumber);
      this.cardExpiry.mount(this.$refs.cardExpiry);
      this.cardCvc.mount(this.$refs.cardCvc);

      // Listen for validation errors or changes
      this.cardNumber.on("change", this.onChange);
      this.cardExpiry.on("change", this.onChange);
      this.cardCvc.on("change", this.onChange);
    },
    
    onChange({ error, elementType, complete, brand }) {
      if (error) {
        this.cardError = error.message;
      } else {
        this.cardError = null;
        // Update completion status based on elementType
        if (elementType === 'cardNumber') {
          this.cardNumberComplete = complete;
          if (brand) {
            this.currentCardBrand = brand;
          }
        } else if (elementType === 'cardExpiry') {
          this.cardExpiryComplete = complete;
        } else if (elementType === 'cardCvc') {
          this.cardCvcComplete = complete;
        }

        // When an input field is complete, focus the next one
        if (complete) {
          this.focusNextField(elementType)
        }

        this.checkAndConfirmSetupIntent();
      }
    },
    
    onZipChange() {
      this.$validator.validate('postalCode', this.addressZip).then((valid) => {
        this.postalCodeComplete = valid;
        this.checkAndConfirmSetupIntent();
      });
    },
    
    checkAndConfirmSetupIntent() {
      // Check if all fields are complete
      if (this.cardNumberComplete && this.cardExpiryComplete &&
        this.cardCvcComplete && this.postalCodeComplete &&
        !this.creatingPaymentMethod) {

        this.confirmSetupIntent();
      } else if (!this.creatingPaymentMethod) {
        // Nothing is pending any more, so stop the captcha clock. Leaving it armed
        // fired a red error and a toast at a customer who was calmly retyping.
        this.clearCaptchaTimeout();
        this.awaitingCaptcha = false;
        this.captchaNotice = null;
        this.$emit('token-generated', {token: null, funding: null});
      }
    },

    regenerateToken() {
      // Regenerate token from existing card data after 422 error
      if (this.cardNumberComplete && this.cardExpiryComplete &&
        this.cardCvcComplete && this.postalCodeComplete) {
        this.confirmSetupIntent();
      }
    },
    
    async confirmSetupIntent() {
      if (this.creatingPaymentMethod) {
        return;
      }

      // Park the submit until a token is in hand; onTurnstileVerified resumes it.
      if (!this.ensureTurnstileToken()) {
        return;
      }
      
      // Prevent rapid-fire calls
      this.creatingPaymentMethod = true;
      
      // Always create a fresh Setup Intent for each tokenization attempt
      // This ensures we never reuse a confirmed Setup Intent
      this.setupIntentClientSecret = null;
      
      await this.createSetupIntent();
      if (!this.setupIntentClientSecret) {
        // Failed to create setup intent
        this.creatingPaymentMethod = false;
        return;
      }

      this.updateElementsDisabledState(true);
      
      try {
        // Step 1: Create payment method with custom fields
        const {error: pmError, paymentMethod} = await this.stripe.createPaymentMethod({
          type: 'card',
          card: this.cardNumber,
          billing_details: {
            address: {
              postal_code: this.addressZip
            }
          }
        });

        if (pmError) {
          this.processingErrorMessage = pmError.message;
          this.notifyError(pmError.message);
          this.creatingPaymentMethod = false;
          this.updateElementsDisabledState(false);
          // Clear setup intent so a new one is created on next attempt
          this.setupIntentClientSecret = null;
          return;
        }

        // Step 2: Confirm Setup Intent with payment method ID
        const {error, setupIntent} = await this.stripe.confirmSetupIntent(this.setupIntentClientSecret, {
          payment_method: paymentMethod.id
        });

        if (error) {
          this.processingErrorMessage = error.message;
          this.notifyError(error.message);
          this.creatingPaymentMethod = false;
          this.updateElementsDisabledState(false);
          // Clear setup intent so a new one is created on next attempt
          this.setupIntentClientSecret = null;
        } else {
          // Use the payment method we created
          this.createStripeToken({
            id: paymentMethod.id,
            card: {
              last4: paymentMethod.card.last4,
              funding: paymentMethod.card.funding
            }
          });
          // Clear setup intent after successful use
          this.setupIntentClientSecret = null;
        }
        
      } catch (error) {
        this.processingErrorMessage = `Error: ${error.message || 'An error occurred while setting up the payment method!'}`;
        this.notifyError("An error occurred while setting up the payment method.");
        this.creatingPaymentMethod = false;
        this.updateElementsDisabledState(false);
        // Clear setup intent so a new one is created on next attempt
        this.setupIntentClientSecret = null;
      }
    },
    
    updateElementsDisabledState(disabled) {
      // Update the disabled state of Stripe elements
      if (this.cardNumber) {
        this.cardNumber.update({ disabled: disabled });
      }
      if (this.cardExpiry) {
        this.cardExpiry.update({ disabled: disabled });
      }
      if (this.cardCvc) {
        this.cardCvc.update({ disabled: disabled });
      }
    },
    
    focusNextField(elementType) {
      const focusOrder = ['cardNumber', 'cardExpiry', 'cardCvc', 'zip'];
      const currentIndex = focusOrder.indexOf(elementType);
      const nextIndex = currentIndex + 1;

      const nextElementKey = focusOrder[nextIndex];
      const nextElement = this[nextElementKey];

      if (elementType === 'cardCvc') {
        if (this.currentCardBrand !== 'amex') {
          this.$refs.addressZip.focus();
        }
      } else {
        if (nextElement) {
          nextElement.focus()
        }
      }
    },
    
    createStripeToken(paymentMethodData) {
      
      // Reuse existing endpoint but pass payment method ID as "token"
      this.$http.post("/api/v1/public/stripe_tokens", {
        token: paymentMethodData.id,
        last4: paymentMethodData.card.last4,
        // Stored on the card's metadata so GenericCard#zip_code can read it back.
        address_zip: this.addressZip
      }).then(response => {
        this.creatingPaymentMethod = false;
        this.updateElementsDisabledState(false);
        this.processingErrorMessage = null;
        this.$emit('token-generated', {
          token: response.data.token, 
          funding: paymentMethodData.card.funding
        });
      }, (error) => {
        this.creatingPaymentMethod = false;
        this.updateElementsDisabledState(false);
        this.notifyError("An error occurred while saving the payment method.");
        this.processingErrorMessage = `Error saving payment method: ${error.statusText || 'Unknown error'}`;
      })
    }
  },
  computed: {
    // Turnstile's 'auto' follows the visitor's OS, which strands a white widget on
    // the dark proof-of-service and contract screens. Those already pass a light
    // foreground colour because the surface behind them is dark, so read that
    // rather than threading a theme prop down three separate parent chains.
    turnstileTheme() {
      return this.hasLightForeground ? 'dark' : 'light'
    },
    hasLightForeground() {
      const colour = (this.foregroundColor || '').trim().toLowerCase();

      if (colour === 'white') {
        return true;
      }

      const hex = colour.replace('#', '');
      if (!/^([0-9a-f]{3}|[0-9a-f]{6})$/.test(hex)) {
        return false;
      }

      const full = hex.length === 3 ? hex.split('').map(c => c + c).join('') : hex;
      const r = parseInt(full.slice(0, 2), 16);
      const g = parseInt(full.slice(2, 4), 16);
      const b = parseInt(full.slice(4, 6), 16);

      // Rec. 601 luma: light text implies a dark surface behind it.
      return (0.299 * r + 0.587 * g + 0.114 * b) > 150;
    },
    isPartialCanadianZip() {
      return /^[A-Za-z]/.test(this.addressZip);
    },
    isPartialUSZip() {
      return /^[0-9]{1,5}(-[0-9]{1,4})?$/.test(this.addressZip);
    },
    zipLength() {
      if (this.isPartialCanadianZip) {
        return 7
      } else if (this.isPartialUSZip) {
        return 5
      } else {
        return 1000
      }
    },
    zipLabel() {
      if (this.isPartialCanadianZip) {
        return "Zip (CAN)";
      } else if (this.isPartialUSZip) {
        return "Zip (US)";
      } else {
        return "Zip";
      }
    },
    zipMask() {
      if (this.isPartialCanadianZip) {
        return "A#A #A#";
      } else if (this.isPartialUSZip) {
        return "#####";
      } else {
        return "#####";
      }
    }
  }
}
</script>
<style scoped>
.stripe-input {
  display: block;
  margin-top: 5px;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
}

.zip-input {
  display: block !important;
  margin-top: 5px !important;
  padding: 10px !important;
  border: 1px solid #ccc !important;
  border-radius: 5px !important;
  font-size: 14px !important;
  box-sizing: border-box !important;
  outline: none !important;
  height: auto !important;
}

.zip-input::placeholder {
  color: #AAB7C4;
}

.zip-input::-webkit-input-placeholder {
  color: #AAB7C4;
}

.zip-input::-moz-placeholder {
  color: #AAB7C4;
  opacity: 1;
}

.zip-input:-ms-input-placeholder {
  color: #AAB7C4;
}

.zip-input:-moz-placeholder {
  color: #AAB7C4;
  opacity: 1;
}

.zip-input:focus {
  border-bottom: 1px solid #ccc !important;
  box-shadow: none !important;
}

.zip-input.invalid {
  border-bottom: 1px solid #ccc !important;
  box-shadow: none !important;
}

.zip-input.valid {
  border-bottom: 1px solid #ccc !important;
  box-shadow: none !important;
}

.disabled-form {
  opacity: 0.7;
  pointer-events: none;
}

.disabled-input {
  background-color: #f5f5f5 !important;
  cursor: not-allowed !important;
}
</style>
